import { useState } from "react";
import {
  FaExchangeAlt,
  FaExternalLinkAlt,
  FaRegFileAlt,
  FaSearch,
  FaUndoAlt,
} from "react-icons/fa";
import { API_ERROR_MESSAGES } from "../constants/api";
import { useAuth } from "../context/AuthContext";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import useScrollToTopOnMessage from "../hooks/useScrollToTopOnMessage";
import {
  fetchCrifBureauReports,
  pullCrifBureauReport,
} from "../services/crifBureauApi";

const inputClass =
  "h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]";

const buttonBaseClass =
  "inline-flex h-[38px] items-center justify-center gap-2 rounded-md px-4 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed";

const emptyFormValues = {
  phone: "",
  contactCategory: "",
  productInterested: "",
  firstName: "",
  middleName: "",
  lastName: "",
  fatherName: "",
  dob: "",
  pan: "",
  city: "",
  state: "",
  localityAddress: "",
  pinCode: "",
  country: "INDIA",
  email: "",
};

const contactCategoryOptions = ["Contractor", "Dealer", "WSS"];

const productOptions = [
  "Group Personal Loan",
  "Personal Loan",
  "Prabal Loan",
  "Business Loan",
  "Dealer Loan",
];

const stateOptions = [
  { value: "KA", label: "Karnataka (KA)" },
  { value: "MH", label: "Maharashtra (MH)" },
  { value: "TS", label: "Telangana (TS)" },
  { value: "GJ", label: "Gujarat (GJ)" },
];

function Field({ label, required = false, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-base">
        {label}
        {required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

function TextField({
  label,
  name,
  value,
  onChange,
  required = false,
  type = "text",
  placeholder = "",
  className = "",
  inputMode,
  maxLength,
}) {
  return (
    <Field label={label} required={required} className={className}>
      <input
        className={inputClass}
        inputMode={inputMode}
        maxLength={maxLength}
        name={name}
        placeholder={placeholder}
        required={required}
        type={type}
        value={value}
        onChange={onChange}
      />
    </Field>
  );
}

function SelectField({
  label,
  name,
  value,
  onChange,
  required = false,
  placeholder = "-- Select --",
  options = [],
  className = "",
}) {
  return (
    <Field label={label} required={required} className={className}>
      <select
        className={inputClass}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => {
          const optionValue =
            typeof option === "string" ? option : option.value;
          const optionLabel =
            typeof option === "string" ? option : option.label;

          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </Field>
  );
}

function DobField({ value, onChange }) {
  return (
    <Field label="DOB" required>
      <input
        className={inputClass}
        name="dob"
        required
        type="date"
        value={value}
        onChange={onChange}
      />
    </Field>
  );
}

function ReportCard({ report }) {
  const reportDetails = [
    ["Product", report.productType],
    ["Score", report.scoreValue],
    ["Issued", report.dateOfIssue],
    ["Phone", report.phone],
    ["Source", report.source],
    ["Status", report.status],
  ].filter(
    ([, value]) => value !== undefined && value !== null && value !== "",
  );

  const handleOpenReport = () => {
    if (!report.htmlReportUrl) {
      return;
    }

    window.open(report.htmlReportUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="flex h-full flex-col rounded-md border border-[#d1d5db] bg-white px-5 py-5 shadow-[0_2px_8px_rgba(15,23,42,0.12)]">
      <h2 className="mb-3 flex items-center gap-2 break-all text-base font-bold text-[#111827]">
        <FaRegFileAlt
          className="h-4 w-4 flex-none text-[#d8c9e8]"
          aria-hidden="true"
        />
        {report.title}
      </h2>
      <p className="mb-3 text-base text-[#374151]">
        Created: {report.createdAt}
      </p>
      {reportDetails.length > 0 && (
        <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm text-[#374151]">
          {reportDetails.map(([label, value]) => (
            <div className="contents" key={label}>
              <dt className="font-medium">{label}:</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <button
        className="mt-auto inline-flex h-[38px] w-full items-center justify-center gap-2 rounded bg-[#198754] px-4 text-base font-medium text-white transition hover:bg-[#157347] focus-visible:bg-[#157347] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9dd4b6] disabled:bg-[#8bbfa3]"
        disabled={!report.htmlReportUrl}
        type="button"
        onClick={handleOpenReport}
      >
        <FaExternalLinkAlt
          className="h-3.5 w-3.5 flex-none"
          aria-hidden="true"
        />
        Open New Tab
      </button>
    </section>
  );
}

const CRIF_REQUEST_ID_COUNTER_KEY = "crifBureauRequestIdCounter";

function nextCrifBureauRequestId() {
  const currentCounter = Number.parseInt(
    window.localStorage.getItem(CRIF_REQUEST_ID_COUNTER_KEY) || "0",
    10,
  );
  const nextCounter = (Number.isFinite(currentCounter) ? currentCounter : 0) + 1;

  window.localStorage.setItem(
    CRIF_REQUEST_ID_COUNTER_KEY,
    String(nextCounter),
  );

  return `pulse_${nextCounter}`;
}

function buildCrifBureauPayload(values, username) {
  return {
    requestId: nextCrifBureauRequestId(),
    phone: values.phone.trim(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    fatherName: values.fatherName.trim(),
    dob: values.dob,
    pan: values.pan.trim().toUpperCase(),
    city: values.city.trim(),
    state: values.state,
    country: values.country.trim(),
    pinCode: values.pinCode.trim(),
    address: values.localityAddress.trim(),
    productInterested: values.productInterested,
    agent: username,
    contactCategory: values.contactCategory,
  };
}

function formatCreatedAt(value) {
  if (!value) {
    return "N/A";
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? value : parsedDate.toLocaleString();
}

function formatIssuedDate(value) {
  if (!value) {
    return "";
  }

  const isoDateMatch = String(value).match(/^\d{4}-\d{2}-\d{2}/);

  return isoDateMatch ? isoDateMatch[0] : value;
}

function firstReportValue(...values) {
  return values.find(
    (value) => value !== undefined && value !== null && value !== "",
  );
}

function mapCrifBureauReport(data) {
  const responseData = data?.data || data || {};
  const providerResponse =
    responseData.providerResponse || responseData.provider_response || {};
  const report = providerResponse.report || responseData.report || responseData;
  const files = providerResponse.files || responseData.files || report.files || {};
  const score =
    providerResponse.score && typeof providerResponse.score === "object"
      ? providerResponse.score
      : {};
  const codifiedSummary =
    responseData.codifiedSummary || responseData.codified_summary || {};
  const htmlReportUrl = firstReportValue(
    files.htmlReportUrl,
    files.html_report_url,
    report.htmlReportUrl,
    report.html_report_url,
    responseData.htmlReportUrl,
    responseData.html_report_url,
  );
  const reportReference = firstReportValue(
    report.reportId,
    report.report_id,
    responseData.reportId,
    responseData.report_id,
    codifiedSummary.report_id,
  );
  const reportId =
    firstReportValue(
      reportReference,
      files.fileId,
      files.file_id,
      htmlReportUrl,
    ) || `crif-report-${Date.now()}`;
  const dateOfIssue = firstReportValue(
    report.dateOfIssue,
    report.date_of_issue,
    responseData.dateOfIssue,
    responseData.date_of_issue,
  );
  const createdAt = firstReportValue(
    report.createdAt,
    report.created_at,
    responseData.createdAt,
    responseData.created_at,
    codifiedSummary.created_at,
    codifiedSummary.report_date,
    dateOfIssue,
  );

  return {
    id: reportId,
    title: reportReference
      ? `Report ${reportReference}`
      : "CRIF Bureau Report",
    createdAt: formatCreatedAt(createdAt),
    htmlReportUrl,
    source: firstReportValue(
      responseData.source,
      providerResponse.source,
      report.source,
    ),
    status: report.status,
    productType: firstReportValue(report.productType, report.product_type),
    scoreValue: firstReportValue(
      score.bureauScore,
      score.bureau_score,
      report.scoreValue,
      report.score_value,
      codifiedSummary.score,
      typeof responseData.score !== "object" ? responseData.score : undefined,
    ),
    dateOfIssue: formatIssuedDate(dateOfIssue),
    phone: firstReportValue(
      report.phone,
      responseData.phone,
      codifiedSummary.phone,
    ),
  };
}

function CrifBureauSearchPage() {
  const { user } = useAuth();
  const [formValues, setFormValues] = useState(emptyFormValues);
  const [capturedMessage, setCapturedMessage] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [searchError, setSearchError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [reports, setReports] = useState([]);

  usePageTitle("CRIF Bureau Search | Pargro");
  useScrollToTopOnMessage(
    [capturedMessage, searchMessage, searchError, submitError]
      .filter(Boolean)
      .join("|"),
  );

  const updateFormValue = (event) => {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  };

  const handlePhoneSearch = async () => {
    const phone = formValues.phone.trim();

    if (!phone) {
      setReports([]);
      setCapturedMessage("");
      setSearchMessage("");
      setSubmitError("");
      setSearchError(
        "Please enter a phone number to search saved CRIF reports.",
      );
      return;
    }

    setIsSearching(true);
    setCapturedMessage("");
    setSearchMessage("");
    setSearchError("");
    setSubmitError("");

    try {
      const result = await fetchCrifBureauReports(phone);
      const savedReports = result.data.map(mapCrifBureauReport);

      setReports(savedReports);
      setSearchMessage(
        savedReports.length === 0
          ? "No saved CRIF reports found for this phone."
          : savedReports.length === 1
            ? "Found 1 saved CRIF report."
            : `Found ${savedReports.length} saved CRIF reports.`,
      );
    } catch (error) {
      setReports([]);
      setSearchError(
        error.message || API_ERROR_MESSAGES.crifReportsFetchFailed,
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setCapturedMessage("");
    setSubmitError("");
    setSearchMessage("");
    setSearchError("");

    try {
      const result = await pullCrifBureauReport(
        buildCrifBureauPayload(formValues, user?.username || ""),
      );

      setReports([mapCrifBureauReport(result.data)]);
      setCapturedMessage("Captured values and fetched CRIF data successfully.");
    } catch (error) {
      setReports([]);
      setSubmitError(error.message || API_ERROR_MESSAGES.crifPullFetchFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormValues(emptyFormValues);
    setCapturedMessage("");
    setSearchMessage("");
    setSearchError("");
    setSubmitError("");
    setReports([]);
  };

  return (
    <ActionPageLayout title="CRIF Bureau Search">
      <form className="mt-6" onSubmit={handleSubmit} onReset={handleReset}>
        {capturedMessage && (
          <div
            className="mb-4 rounded-md border border-[#9dd4b6] bg-[#d8efe3] px-4 py-4 text-base text-[#064e3b]"
            role="status"
          >
            {capturedMessage}
          </div>
        )}

        {submitError && (
          <div
            className="mb-4 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-base text-[#842029]"
            role="alert"
          >
            {submitError}
          </div>
        )}

        <div className="grid grid-cols-3 gap-x-5 gap-y-6 max-[960px]:grid-cols-1">
          <Field label="Phone" required>
            <div className="flex h-[38px]">
              <input
                className="h-full min-w-0 flex-1 rounded-l-md border border-r-0 border-[#d8dee6] bg-white px-3 text-base outline-none transition focus:z-10 focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]"
                inputMode="numeric"
                maxLength={10}
                name="phone"
                required
                type="tel"
                value={formValues.phone}
                onChange={updateFormValue}
              />
              <button
                className="inline-flex h-full items-center justify-center gap-2 rounded-r-md border border-[#0d73ff] bg-white px-4 text-base font-medium text-[#0d63ff] transition hover:bg-[#eff6ff] focus-visible:bg-[#eff6ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff] disabled:cursor-not-allowed disabled:opacity-70"
                disabled={isSearching}
                type="button"
                onClick={handlePhoneSearch}
              >
                <FaSearch className="h-4 w-4 flex-none" aria-hidden="true" />
                {isSearching ? "Searching..." : "Search"}
              </button>
            </div>
            <span className="mt-2 block max-w-[21rem] text-base leading-[1.45] text-[#4b5563]">
              Search looks for the latest saved report for this phone.
            </span>
          </Field>

          <SelectField
            label="Contact category"
            name="contactCategory"
            options={contactCategoryOptions}
            value={formValues.contactCategory}
            onChange={updateFormValue}
          />

          <SelectField
            label="Product Interested"
            name="productInterested"
            required
            options={productOptions}
            placeholder="-- Select Product --"
            value={formValues.productInterested}
            onChange={updateFormValue}
          />

          <TextField
            label="First Name"
            name="firstName"
            required
            value={formValues.firstName}
            onChange={updateFormValue}
          />
          <TextField
            label="Middle Name"
            name="middleName"
            value={formValues.middleName}
            onChange={updateFormValue}
          />
          <TextField
            label="Last Name"
            name="lastName"
            required
            value={formValues.lastName}
            onChange={updateFormValue}
          />

          <TextField
            label="Father's Name"
            name="fatherName"
            required
            value={formValues.fatherName}
            onChange={updateFormValue}
          />
          <DobField value={formValues.dob} onChange={updateFormValue} />
          <TextField
            label="PAN"
            name="pan"
            required
            maxLength={10}
            value={formValues.pan}
            onChange={updateFormValue}
          />

          <TextField
            label="City"
            name="city"
            required
            value={formValues.city}
            onChange={updateFormValue}
          />
          <SelectField
            label="State (2-letter)"
            name="state"
            required
            options={stateOptions}
            value={formValues.state}
            onChange={updateFormValue}
          />
          <div aria-hidden="true" className="max-[960px]:hidden" />

          <TextField
            label="Locality/Full-Address"
            name="localityAddress"
            required
            className="col-span-2 max-[960px]:col-span-1"
            value={formValues.localityAddress}
            onChange={updateFormValue}
          />
          <TextField
            label="PIN Code"
            name="pinCode"
            required
            inputMode="numeric"
            value={formValues.pinCode}
            onChange={updateFormValue}
          />

          <TextField
            label="Country"
            name="country"
            required
            value={formValues.country}
            onChange={updateFormValue}
          />
          <Field label="Email (optional)">
            <input
              className={inputClass}
              name="email"
              type="email"
              value={formValues.email}
              onChange={updateFormValue}
            />
          </Field>
          <div aria-hidden="true" className="max-[960px]:hidden" />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            className={`${buttonBaseClass} bg-[#1773f8] text-white shadow hover:bg-[#0d63dc] focus-visible:bg-[#0d63dc] focus-visible:ring-[#9fc8ff]`}
            disabled={isSubmitting}
            type="submit"
          >
            <FaSearch className="h-4 w-4 flex-none" aria-hidden="true" />
            {isSubmitting ? "Fetching Info..." : "Check CRIF Info"}
          </button>
          <button
            className={`${buttonBaseClass} border border-[#6c757d] bg-white text-[#4b5563] hover:bg-[#f8fafc] focus-visible:bg-[#f8fafc] focus-visible:ring-[#cbd5e1]`}
            type="reset"
          >
            <FaUndoAlt className="h-4 w-4 flex-none" aria-hidden="true" />
            Reset
          </button>
          <button
            className={`${buttonBaseClass} bg-[#ffc107] text-[#111827] hover:bg-[#f0b400] focus-visible:bg-[#f0b400] focus-visible:ring-[#fde68a]`}
            type="button"
          >
            <FaExchangeAlt className="h-4 w-4 flex-none" aria-hidden="true" />
            Show Differences
          </button>
        </div>

        {searchError && (
          <div
            className="mt-5 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
            role="alert"
          >
            {searchError}
          </div>
        )}

        {searchMessage && (
          <div
            className="mt-5 rounded-md border border-[#9eeaf9] bg-[#cff4fc] px-5 py-5 text-base text-[#055160]"
            role="status"
          >
            {searchMessage}
          </div>
        )}

        {reports.length > 0 && (
          <div className="mt-8 grid grid-cols-3 items-stretch gap-5 max-[1180px]:grid-cols-2 max-[760px]:grid-cols-1">
            {reports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
        )}
      </form>
    </ActionPageLayout>
  );
}

export default CrifBureauSearchPage;
